import { useState } from 'react'
import { STATUSES } from '@hub/shared'
import type { Status } from '@hub/shared'
import { STATUS_TONE, statusLabel } from '../components/Brand.js'
import { FilterBar } from '../components/FilterBar.js'
import { ItemCard } from '../components/ItemCard.js'
import { PageHeader } from '../components/PageHeader.js'
import { toQuery, useFilters, useSheetParam } from '../lib/filters.js'
import { useItems, useUpdateItem } from '../lib/items.js'

/** The board: one column per stage. Cards move by drag and drop or in the detail sheet. */
export function Pipeline(): JSX.Element {
  const { filters } = useFilters()
  const { data: items = [], isLoading } = useItems(toQuery(filters))
  const update = useUpdateItem()
  const { openItem, openNew } = useSheetParam()
  const [showDiscarded, setShowDiscarded] = useState(false)
  const [over, setOver] = useState<Status>()
  // Phone: one stage at a time, picked from a segmented strip — five columns do not fit 375 pt.
  const [stage, setStage] = useState<Status>('in_arbeit')

  const columns = STATUSES.filter((s) => showDiscarded || s !== 'verworfen')
  const discarded = items.filter((i) => i.status === 'verworfen').length

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-4 px-5 pt-6 md:px-8">
        <PageHeader
          title="Pipeline"
          subtitle={`${items.length - (showDiscarded ? 0 : discarded)} Einträge`}
          action={
            discarded > 0 ? (
              <button type="button" onClick={() => setShowDiscarded((x) => !x)} className="text-small text-label2/60 hover:text-pebro">
                {showDiscarded ? 'Verworfene ausblenden' : `${discarded} verworfen anzeigen`}
              </button>
            ) : undefined
          }
        />
        <FilterBar />
      </div>

      {/* Phone, iPad and narrow windows: one stage at a time */}
      <div className="xl:hidden">
        <div className="flex gap-1.5 overflow-x-auto px-5 pb-1 pt-4 [scrollbar-width:none]">
          {columns.map((s) => {
            const n = items.filter((i) => i.status === s).length
            const on = stage === s
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStage(s)}
                className={`flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-small transition-colors ${on ? 'bg-label font-semibold text-cell' : 'bg-cell text-label2/80'}`}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: STATUS_TONE[s] }} />
                {statusLabel(s)}
                <span className={`num ${on ? 'text-cell/60' : 'text-label2/45'}`}>{n}</span>
              </button>
            )
          })}
        </div>
        <div className="space-y-2 px-5 pb-6 pt-3">
          {items
            .filter((i) => i.status === stage)
            .map((item) => (
              <ItemCard key={item.id} item={item} onOpen={() => openItem(item.id)} />
            ))}
          {!isLoading && items.every((i) => i.status !== stage) && (
            <p className="py-10 text-center text-small text-label2/50">Nichts in „{statusLabel(stage)}“.</p>
          )}
        </div>
      </div>

      {/* Mac: the board — columns share the width, nothing scrolls sideways */}
      <div
        className="hidden min-h-0 flex-1 gap-3 px-5 pb-6 pt-5 md:px-8 xl:grid"
        style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
      >
        {columns.map((status) => {
          const cards = items.filter((i) => i.status === status)
          return (
            <section
              key={status}
              onDragOver={(e) => {
                e.preventDefault()
                setOver(status)
              }}
              onDragLeave={() => setOver((s) => (s === status ? undefined : s))}
              onDrop={(e) => {
                e.preventDefault()
                setOver(undefined)
                const id = e.dataTransfer.getData('text/item-id')
                const item = items.find((i) => i.id === id)
                if (item && item.status !== status) update.mutate({ id, patch: { status } })
              }}
              className={`flex min-w-0 flex-col rounded-[22px] p-2 transition-colors ${
                over === status ? 'bg-pebro/10 ring-2 ring-pebro/30' : 'bg-fill/[0.06]'
              }`}
            >
              <header className="flex items-center gap-2 px-2 pb-2 pt-1.5">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_TONE[status] }} />
                <h2 className="text-small font-semibold text-label">{statusLabel(status)}</h2>
                <span className="num text-caption text-label2/50">{cards.length}</span>
                {status === 'idee' && (
                  <button
                    type="button"
                    onClick={() => openNew()}
                    className="ml-auto grid h-7 w-7 place-items-center rounded-full text-label2/60 hover:bg-fill/[0.12] hover:text-pebro"
                    aria-label="Neuer Eintrag"
                  >
                    +
                  </button>
                )}
              </header>
              <div className="min-h-[6rem] min-w-0 flex-1 space-y-2 overflow-y-auto overflow-x-hidden">
                {cards.map((item) => (
                  <ItemCard key={item.id} item={item} draggable onOpen={() => openItem(item.id)} />
                ))}
                {cards.length === 0 && !isLoading && (
                  <p className="px-2 py-6 text-center text-caption text-label2/40">Hierher ziehen</p>
                )}
              </div>
            </section>
          )
        })}
      </div>
    </div>
  )
}
